import { useEffect, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrendingUp, Plus, ExternalLink, Loader2 } from 'lucide-react';
import {
  getCommodityBidMe,
  getPendingCommodityBidItems,
  kindBadgeClass,
  kindLabel,
} from '@/services/commoditybidIntegration';
import CommodityBidItemDetails from '@/components/customer/CommodityBidItemDetails';
import { COMMODITYBID_CREATE_REQUEST_URL } from '@/config/portalUrls';

function formatDateShort(value) {
  if (value == null || value === '') return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatAmount(item) {
  if (item.amount == null || item.amount === '') return null;
  const n = Number(item.amount);
  if (Number.isNaN(n)) return String(item.amount);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function PendingItemCard({ item, testId }) {
  const amount = formatAmount(item);
  const isRequest = item.kind === 'production_request';

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-5 transition-shadow hover:shadow-sm"
      data-testid={testId}
    >
      <CardHeader item={item} isRequest={isRequest} amount={amount} />
      <CommodityBidItemDetails item={item} />
    </div>
  );
}

function CardHeader({ item, isRequest, amount }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="min-w-0 flex-1">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${kindBadgeClass(item.kind)}`}
          >
            {kindLabel(item.kind)}
          </span>
          {isRequest && (
            <span className="rounded bg-sky-50 px-2 py-0.5 text-[10px] font-semibold uppercase text-sky-700">
              Awaiting supplier bids
            </span>
          )}
        </div>
        <p className="font-heading text-lg font-bold text-slate-900">{item.title}</p>
        {!isRequest && item.supplierName && (
          <p className="mt-0.5 text-sm text-slate-600">{item.supplierName}</p>
        )}
        {item.notes && <p className="mt-1 line-clamp-2 text-sm text-slate-500">{item.notes}</p>}
      </div>
      <div className="shrink-0 text-right">
        {amount != null && !isRequest && (
          <>
            <p className="font-heading text-2xl font-bold text-orange-600">
              {item.currency === 'USD' ? '$' : ''}
              {amount}
            </p>
            <p className="text-xs text-slate-400">{item.currency}</p>
          </>
        )}
        <p className="mt-1 text-xs capitalize text-slate-500">{item.status}</p>
        <p className="text-xs text-slate-400">{formatDateShort(item.createdAt)}</p>
      </div>
    </div>
  );
}

export default function LiveBids() {
  const { user } = useAuth();
  const loginEmail = (user?.email || '').trim().toLowerCase();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError(false);
      try {
        const res = await getCommodityBidMe();
        if (cancelled) return;
        setItems(getPendingCommodityBidItems(res.data));
      } catch {
        if (!cancelled) {
          setLoadError(true);
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const productionRequests = items.filter((i) => i.kind === 'production_request');
  const supplierResponses = items.filter((i) => i.kind === 'bid' || i.kind === 'offer');

  return (
    <div className="space-y-5" data-testid="live-bids-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900">Pending Bids</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            Your open production requests and supplier bids awaiting your review
          </p>
        </div>
        <a
          href={COMMODITYBID_CREATE_REQUEST_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 rounded-xl bg-orange-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-600"
          data-testid="pending-bids-create-request"
        >
          <Plus className="h-4 w-4" />
          Create Bid
          <ExternalLink className="h-3.5 w-3.5 opacity-80" />
        </a>
      </div>

      {loading && (
        <div className="flex flex-col items-center gap-3 py-16" data-testid="pending-bids-loading">
          <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
          <p className="text-sm text-slate-500">Loading pending bids…</p>
        </div>
      )}

      {!loading && loadError && (
        <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-center text-sm text-red-800">
          CommodityBid data could not be loaded.
        </div>
      )}

      {!loading && !loadError && items.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
          <TrendingUp className="mx-auto mb-3 h-10 w-10 text-slate-300" />
          <p className="font-medium text-slate-500">No pending bids</p>
          <p className="mt-1 text-sm text-slate-400">
            Create a production request on CommodityBid to start receiving supplier bids.
          </p>
          {loginEmail ? (
            <p className="mt-3 text-xs text-slate-500">
              CommodityBid is matched to your login email:{' '}
              <span className="font-medium text-slate-700">{loginEmail}</span>
            </p>
          ) : null}
          <a
            href={COMMODITYBID_CREATE_REQUEST_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-orange-600"
          >
            <Plus className="h-4 w-4" />
            Create Bid
            <ExternalLink className="h-3.5 w-3.5 opacity-80" />
          </a>
        </div>
      )}

      {!loading && !loadError && productionRequests.length > 0 && (
        <section className="space-y-3" data-testid="pending-production-requests">
          <h2 className="text-sm font-semibold text-slate-800">Your open requests</h2>
          <div className="grid gap-4">
            {productionRequests.map((item) => (
              <PendingItemCard key={`pr-${item.id}`} item={item} testId={`production-request-${item.id}`} />
            ))}
          </div>
        </section>
      )}

      {!loading && !loadError && supplierResponses.length > 0 && (
        <section className="space-y-3" data-testid="pending-supplier-bids">
          <h2 className="text-sm font-semibold text-slate-800">Supplier bids & offers</h2>
          <div className="grid gap-4">
            {supplierResponses.map((item) => (
              <PendingItemCard
                key={`${item.kind}-${item.id}`}
                item={item}
                testId={`supplier-${item.kind}-${item.id}`}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
